import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiGet, apiPut, apiPost, fmtDate } from "@/lib/shieldx";
import { PageHeader, Loader } from "@/components/shieldx/Primitives";
import { Table } from "@/components/shieldx/Table";
import { StatusBadge, RiskBadge } from "@/components/shieldx/Badges";
import {
  SecBadge, PencilBtn, ActionMenu, ActionDialog, EditDialog, Drawer, Timeline, FilterBar, KV, Section,
} from "@/components/shieldx/SuperAdmin";
import { EmailLink } from "@/components/shieldx/SmartComposer";
import {
  Building2, Eye, BadgeCheck, Ban, RotateCcw, ShieldX, CheckCircle2, ShieldAlert,
} from "lucide-react";

const ORG_EDIT_FIELDS = [
  { key: "name", label: "Display Name" },
  { key: "legal_name", label: "Legal Name" },
  { key: "type", label: "Organization Type" },
  { key: "industry", label: "Industry" },
  { key: "website", label: "Website" },
  { key: "country", label: "Country" },
  { key: "state", label: "State" },
  { key: "city", label: "City" },
  { key: "security_classification", label: "Security Classification" },
  { key: "description", label: "Description", type: "textarea" },
];

export default function SAOrganizations({ category, title = "Organizations", subtitle, icon = Building2 }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [edit, setEdit] = useState(null);
  const [action, setAction] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (search) params.set("q", search);
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    setRows(await apiGet(`/super-admin/organizations?${params.toString()}`));
  }, [category, search, filters]);

  useEffect(() => { const t = setTimeout(load, 220); return () => clearTimeout(t); }, [load]);

  const open360 = async (org) => {
    setSel(org); setDetail(null); setDetailLoading(true);
    try { setDetail(await apiGet(`/super-admin/organizations/${org.id}/360`)); }
    finally { setDetailLoading(false); }
  };

  const doAction = (org, act) => setAction({ org, act });
  const runAction = async ({ reason, step_up_token, ...extra }) => {
    const { org, act } = action;
    await apiPost(`/super-admin/organizations/${org.id}/action`, { action: act, reason, step_up_token, ...extra });
    toast.success(`${act} applied to ${org.name}`);
    load(); if (sel?.id === org.id) open360(org);
  };

  const saveEdit = async (vals) => {
    await apiPut(`/super-admin/organizations/${edit.id}`, { ...vals, reason: "Super Admin field edit" });
    toast.success("Organization updated"); load();
  };

  if (!rows) return <Loader label={`Loading ${title.toLowerCase()}`} />;

  const columns = [
    { key: "name", label: "Organization", render: (r) => (
      <div><p className="font-medium text-slate-100">{r.name}</p><p className="font-mono text-[11px] text-slate-500">{r.website || r.id}</p></div>
    ) },
    { key: "type", label: "Type", render: (r) => <span className="text-xs text-slate-400">{r.type}</span> },
    { key: "verification_status", label: "Verification", render: (r) => <StatusBadge value={r.verification_status} /> },
    { key: "security_status", label: "Security", render: (r) => <SecBadge value={r.security_status} /> },
    { key: "asset_count", label: "Assets", render: (r) => <span className="font-mono tabular-nums text-slate-300">{r.asset_count}</span> },
    { key: "active_incidents", label: "Incidents", render: (r) => <span className={r.active_incidents ? "font-mono text-amber-300" : "font-mono text-slate-500"}>{r.active_incidents}</span> },
    { key: "actions", label: "", render: (r) => (
      <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => open360(r)} data-testid={`view360-${r.id}`} className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-700/60 bg-slate-800/50 px-2.5 text-xs text-slate-300 hover:border-blue-500/50 hover:text-blue-300"><Eye className="h-3.5 w-3.5" /> 360°</button>
        <PencilBtn onClick={() => setEdit(r)} testid={`edit-${r.id}`} />
        <ActionMenu testid={`menu-${r.id}`} items={[
          { label: "View As (read-only)", icon: Eye, onClick: () => navigate(`/app/super-admin/view-as/${r.id}`) },
          { label: "Verify", icon: BadgeCheck, onClick: () => doAction(r, "VERIFY") },
          { label: "Re-verify", icon: RotateCcw, onClick: () => doAction(r, "REVERIFY") },
          { label: "Approve", icon: CheckCircle2, onClick: () => doAction(r, "APPROVE") },
          r.lifecycle === "SUSPENDED" || r.lifecycle === "REVOKED"
            ? { label: "Reactivate", icon: RotateCcw, onClick: () => doAction(r, "REACTIVATE") }
            : { label: "Suspend", icon: Ban, danger: true, onClick: () => doAction(r, "SUSPEND") },
          { label: "Revoke", icon: ShieldX, danger: true, onClick: () => doAction(r, "REVOKE") },
        ]} />
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader icon={icon} title={title} subtitle={subtitle || "360° governance. Inline edit, verify, suspend, reactivate and revoke — every action is audited."} />
      <FilterBar searchValue={search} onSearch={setSearch} values={filters}
        onChange={(k, v) => setFilters((f) => ({ ...f, [k]: v }))}
        searchPlaceholder="Search name, domain, email, country…"
        filters={[
          { key: "verification", label: "Verification", options: ["VERIFIED", "UNVERIFIED", "VERIFICATION_IN_PROGRESS"] },
          { key: "protection", label: "Protection", options: ["PROTECTED", "MONITORING", "AT_RISK"] },
          { key: "status", label: "Lifecycle", options: ["SUSPENDED", "REVOKED"] },
        ]} />
      <Table columns={columns} rows={rows} testid="sa-orgs-table" onRowClick={open360} empty="No organizations match these filters" />

      <EditDialog open={!!edit} onClose={() => setEdit(null)} title={`Edit ${edit?.name || "organization"}`}
        fields={ORG_EDIT_FIELDS} initial={edit || {}} onSave={saveEdit} />

      <ActionDialog open={!!action} onClose={() => setAction(null)}
        action={action?.act} targetType="ORGANIZATION" targetId={action?.org?.id}
        title={`${action?.act} — ${action?.org?.name || ""}`}
        body={action?.act === "SUSPEND" ? "Suspending preserves all data, incidents and audit history. Prohibited access will be blocked per policy." : action?.act === "REVOKE" ? "Revocation is a high-impact action. Data and tamper-evident evidence are preserved." : "This governance action will be recorded in the audit chain."}
        extraFields={action?.act === "SUSPEND" ? [{ key: "reactivation_requirements", label: "Reactivation Requirements" }] : []}
        onDone={runAction} />

      <Drawer open={!!sel} onClose={() => { setSel(null); setDetail(null); }} loading={detailLoading}
        title={sel?.name} subtitle={detail?.identity?.id} testid="org-360-drawer"
        badge={detail && <SecBadge value={detail.security.status} />}
        tabs={detail ? org360Tabs(detail) : []} />
    </div>
  );
}

function OrgThreads({ orgId }) {
  const [state, setState] = useState(null);
  useEffect(() => { let a = true; apiGet(`/super-admin/email/org/${orgId}/threads`).then((d) => a && setState(d)).catch(() => a && setState({ configured: false, status: "DEGRADED", threads: [] })); return () => { a = false; }; }, [orgId]);
  if (!state) return <p className="text-sm text-slate-500">Loading mailbox…</p>;
  if (!state.configured || state.status === "NOT_CONFIGURED") return (
    <div className="rounded-lg border border-dashed border-slate-800 p-5 text-center">
      <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Email Integration · Not Configured</p>
      <p className="mt-1 text-xs text-slate-600">Mailbox thread sync is not connected. No email threads are fabricated.</p>
    </div>
  );
  if (!state.threads?.length) return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-5 text-center">
      <p className="font-mono text-[11px] uppercase tracking-widest text-emerald-400/80">IMAP Connected</p>
      <p className="mt-1 text-xs text-slate-500">No mailbox threads associated with this organization{state.domain ? ` (${state.domain})` : ""}.</p>
    </div>
  );
  return (
    <Table testid="org360-threads" empty="No threads" rows={state.threads} columns={[
      { key: "subject", label: "Subject", render: (r) => <div><p className="text-sm text-slate-200">{r.subject}</p><p className="font-mono text-[10px] text-slate-500">{r.from}</p></div> },
      { key: "box", label: "Folder", render: (r) => <span className="text-xs text-slate-400">{r.box}</span> },
      { key: "date", label: "Date", render: (r) => <span className="text-xs text-slate-500">{r.date}</span> },
    ]} />
  );
}

function org360Tabs(d) {
  const i = d.identity, c = d.primary_contact, v = d.verification, s = d.security;
  return [
    { key: "identity", label: "Identity", content: (
      <div className="grid gap-x-8 sm:grid-cols-2">
        <KV label="Organization ID" value={i.id} mono /><KV label="Legal Name" value={i.legal_name} />
        <KV label="Type" value={i.type} /><KV label="Category" value={i.category} />
        <KV label="Industry" value={i.industry} /><KV label="Website" value={i.website} />
        <KV label="Official Domain" value={i.official_domain} /><KV label="Country" value={i.country} />
        <KV label="Tier" value={i.tier} /><KV label="Classification" value={i.security_classification} />
        <div className="sm:col-span-2"><KV label="Description" value={i.description} /></div>
        {i.connected_platforms && <div className="sm:col-span-2"><Section title="Connected Platforms">
          <div className="flex flex-wrap gap-2">{i.connected_platforms.map((p, n) => <span key={n} className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-1.5 text-xs text-slate-300">{p.platform}: <b>{p.handle}</b> · {p.status}</span>)}</div>
        </Section></div>}
      </div>
    ) },
    { key: "contact", label: "Contacts", content: (
      <div className="grid gap-x-8 sm:grid-cols-2">
        <KV label="Name" value={c.name} />
        <div className="flex flex-col gap-0.5 border-b border-slate-800/60 py-2"><span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Email</span><EmailLink email={c.email} entityType="organization" entityId={i.id} context="update" orgId={i.id} /></div>
        <KV label="Phone" value={c.phone} mono /><KV label="Role" value={c.role} />
        <KV label="Department" value={c.department} />
      </div>
    ) },
    { key: "communications", label: "Communications", content: (
      <div>
        <Section title="Smart Email Composer">
          <p className="mb-3 text-sm text-slate-400">Click an authorized email to open the ShieldX Smart Email Composer. Drafts are generated from real entity data; nothing is sent without explicit confirmation.</p>
          <div className="flex flex-wrap gap-2">
            <EmailLink email={c.email} entityType="organization" entityId={i.id} context="verification" orgId={i.id} className="rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2" />
          </div>
        </Section>
        <Section title="Mailbox Threads">
          <OrgThreads orgId={i.id} />
        </Section>
      </div>
    ) },
    { key: "verification", label: "Verification", content: (
      <div className="grid gap-x-8 sm:grid-cols-2">
        <div className="py-2"><span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Organization</span><StatusBadge value={v.organization} /></div>
        <div className="py-2"><span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Domain</span><StatusBadge value={v.domain} /></div>
        <div className="py-2"><span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Ownership</span><StatusBadge value={v.ownership} /></div>
        <KV label="Method" value={v.method} /><KV label="Verified At" value={fmtDate(v.verified_at)} /><KV label="Failures" value={v.failures} />
      </div>
    ) },
    { key: "assets", label: `Assets (${d.assets.length})`, content: (
      <Table testid="org360-assets" empty="No assets" rows={d.assets} columns={[
        { key: "name", label: "Asset", render: (r) => <span className="font-medium text-slate-200">{r.name}</span> },
        { key: "type", label: "Type" }, { key: "environment", label: "Env" },
        { key: "risk_level", label: "Risk", render: (r) => <RiskBadge value={r.risk_level} /> },
        { key: "state", label: "State", render: (r) => <StatusBadge value={r.state || r.protection_status} /> },
      ]} />
    ) },
    { key: "security", label: "Security", content: (
      <div className="grid gap-x-8 sm:grid-cols-2">
        <div className="py-2"><span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Evidence-based Status</span><SecBadge value={s.status} /></div>
        <KV label="Security Rating" value={s.rating} /><KV label="Current Risk" value={s.current_risk} />
        <KV label="Active Incidents" value={s.active_incidents} /><KV label="Historical Incidents" value={s.historical_incidents} />
        <KV label="Lifecycle" value={s.lifecycle} />
        {d.suspension && <div className="sm:col-span-2 mt-3 rounded-lg border border-rose-500/30 bg-rose-500/5 p-3">
          <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-rose-300"><ShieldAlert className="h-3.5 w-3.5" /> {d.suspension.status}</p>
          <p className="mt-1 text-sm text-slate-300">{d.suspension.reason}</p>
          <p className="mt-1 text-xs text-slate-500">By {d.suspension.suspended_by} · {fmtDate(d.suspension.suspended_at)} · Review: {d.suspension.review_status}</p>
        </div>}
      </div>
    ) },
    { key: "incidents", label: `Incidents (${d.incidents.length})`, content: (
      <Table testid="org360-incidents" empty="No incidents" rows={d.incidents} columns={[
        { key: "incident_id", label: "ID", render: (r) => <span className="font-mono text-xs text-slate-300">{r.incident_id}</span> },
        { key: "title", label: "Title" },
        { key: "risk_level", label: "Risk", render: (r) => <RiskBadge value={r.risk_level} /> },
        { key: "state", label: "State", render: (r) => <StatusBadge value={r.state} /> },
      ]} />
    ) },
    { key: "access", label: `Access (${d.access.members.length})`, content: (
      <Table testid="org360-members" empty="No members" rows={d.access.members} columns={[
        { key: "name", label: "Member" }, { key: "email", label: "Email", render: (r) => <span className="font-mono text-xs">{r.email}</span> },
        { key: "role", label: "Role" }, { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status || "ACTIVE"} /> },
      ]} />
    ) },
    { key: "policies", label: `Policies (${d.policies.length})`, content: (
      <Table testid="org360-policies" empty="No policies" rows={d.policies} columns={[
        { key: "name", label: "Policy" }, { key: "scope", label: "Scope" },
        { key: "actions", label: "Actions", render: (r) => <span className="text-xs text-slate-400">{(r.actions || []).join(", ")}</span> },
        { key: "enabled", label: "Enabled", render: (r) => <span className={r.enabled ? "text-emerald-400" : "text-slate-500"}>{r.enabled ? "Yes" : "No"}</span> },
      ]} />
    ) },
    { key: "integrations", label: `Integrations (${d.integrations.length})`, content: (
      <Table testid="org360-integrations" empty="No integrations" rows={d.integrations} columns={[
        { key: "name", label: "Integration" }, { key: "category", label: "Category" },
        { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }, { key: "health", label: "Health" },
      ]} />
    ) },
    { key: "timeline", label: "Timeline", content: <Timeline events={d.timeline} /> },
    { key: "audit", label: "Audit", content: (
      <Table testid="org360-audit" empty="No admin audit yet" rows={d.super_admin_audit} columns={[
        { key: "action", label: "Action", render: (r) => <span className="font-mono text-xs text-slate-200">{r.action}</span> },
        { key: "actor", label: "Actor", render: (r) => <span className="text-xs">{r.actor}</span> },
        { key: "reason", label: "Reason", render: (r) => <span className="text-xs text-slate-400">{r.reason}</span> },
        { key: "timestamp", label: "When", render: (r) => <span className="text-xs text-slate-500">{fmtDate(r.timestamp)}</span> },
      ]} />
    ) },
  ];
}
